<template>
  <div class="container">

    <breadcrumb-group :items="breadcrumbItems"/>

    <form @submit.prevent="submitEditWorkplace" novalidate>
      <heading-one>お勤め先情報の変更</heading-one>

      <errors-alert :errors="errors" column-only="true" :items="inputItemsMb"></errors-alert>

      <wizard-group class="_mb-md-60 _mb-sm-20">
        <main-wizard :active-index="0" type="useremploy"></main-wizard>
      </wizard-group>

      <p>変更内容をご入力のうえ、「確認画面へ」を押してください。</p>

      <section>

        <table-group>
          <heading-two>お勤め先情報</heading-two>

          <table-box>
            <table-body>
              <table-row>
                <table-header badge="必須" badge-size="-sm">職業</table-header>
                <table-data :hasError="errors['work_form']">
                  <input-group :errors="errors" :propNames="['work_form']">
                    <btngroup-radio>
                      <btn-radio v-for="(workForm, index) in workForms" v-model="userData.work_form"
                                 :key="'workForm' + index" :id="'workForm' + index" name="workForm"
                                 :value="workForm['value']" :label="workForm['name']">
                      </btn-radio>
                    </btngroup-radio>
                  </input-group>
                  <template v-if="showWorkFormMessage">
                    <list-group class="-note _mt-10">
                      <list-item class="-note">定期収入のない方はお申し込みいただけません、お勤め先を必ずご記入ください。</list-item>
                    </list-group>
                  </template>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">業種</table-header>
                <table-data :hasError="errors['biz_type']">
                  <input-group :errors="errors" :propNames="['biz_type']">
                    <btn-select v-model="userData.biz_type" id="biz_type">
                      <option v-for="(bizType, index) in bizTypes" :key="'bizType' + index" :value="bizType['value']">{{bizType['name']}}</option>
                    </btn-select>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">職種</table-header>
                <table-data :hasError="errors['job_type']">
                  <input-group :errors="errors" :propNames="['job_type']">
                    <btngroup-radio>
                      <btn-radio v-for="(jobType, index) in jobTypes" v-model="userData.job_type"
                                 :key="'jobType' + index" :id="'jobType' + index" name="jobType"
                                 :value="jobType['value']" :label="jobType['name']">
                      </btn-radio>
                    </btngroup-radio>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">お勤め先<small class="moji">【全角】</small>
                  <btn-tooltip slot="tooltip" target="officeNmTooltip" class="-question"></btn-tooltip>
                  <tooltips-item slot="tooltip" target="officeNmTooltip" placement="righttop">
                    お勤め先名称は（株）（有）など使用せず、正式名称をご記入ください。
                  </tooltips-item>
                </table-header>
                <table-data :hasError="errors['office_nm']">
                  <input-group :errors="errors" :propNames="['office_nm']">
                    <input-group>
                      <input-item v-model="userData.office_nm" type="text" class="-lg" id="office_nm" :maxlength="20"></input-item>
                      <input-label>入力例：auフィナンシャルサービス株式会社</input-label>
                    </input-group>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">お勤め先電話番号<small class="moji">【半角数字】</small>
                </table-header>
                <table-data :hasError="errors['office_phone']">
                  <input-group :errors="errors" :propNames="['office_phone']">
                    <input-phone :phone="{phone1: userData.office_phone_1, phone2: userData.office_phone_2, phone3: userData.office_phone_3}" id="office_phone" @input="setOfficePhone"></input-phone>
                    <input-label>入力例：03-****-****</input-label>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">所属部署<small class="moji">【全角】</small>
                </table-header>
                <table-data :hasError="errors['office_depart']">
                  <input-group :errors="errors" :propNames="['office_depart']">
                    <input-item v-model="userData.office_depart" type="text" class="-md" id="office_depart" :maxlength="20"></input-item>
                    <input-label>入力例： 営業部&nbsp;&nbsp;※所属部署が不明な場合は、「なし」とご記入ください。</input-label>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">入社年月<small class="moji">【半角数字】</small>
                </table-header>
                <table-data :hasError="errors['nsh_ngt']">
                  <input-group :errors="errors" :propNames="['nsh_ngt']">
                    <inputgroup-append>
                      <input-item v-model="nshNgtYyyy" type="text" class="-xs" id="nshNgtYyyy" @change="setNshNgt" :maxlength="4"></input-item>
                      <input-append>年</input-append>
                      <input-item v-model="nshNgtMm" type="text" class="-xs" id="nshNgtMm" @change="setNshNgt" :maxlength="2"></input-item>
                      <input-append>月~</input-append>
                    </inputgroup-append>
                    <input-label>入力例：2018年4月~</input-label>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">社員数
                </table-header>
                <table-data :hasError="errors['staff_num']">
                  <input-group :errors="errors" :propNames="['staff_num']">
                    <btngroup-radio>
                      <btn-radio v-for="(staffNum, index) in staffNums" v-model="userData.staff_num"
                                 :key="'staffNum' + index" :id="'staffNum' + index" name="staffNum"
                                 :value="staffNum['value']" :label="staffNum['name']">
                      </btn-radio>
                    </btngroup-radio>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">
                  年収<small>（税込）<small class="moji">【半角数字】</small></small>
                  <btn-tooltip slot="tooltip" target="grossSalaryTooltip" class="-question"></btn-tooltip>
                  <tooltips-item slot="tooltip" target="grossSalaryTooltip" placement="righttop">
                    収入証明書をご提出いただいた場合、ご入力いただいた年収とは異なる場合があります。
                  </tooltips-item>
                </table-header>
                <table-data :hasError="errors['gross_salary']">
                  <input-group :errors="errors" :propNames="['gross_salary']">
                    <inputgroup-append>
                      <input-item v-model="input_gross_salary" type="text" class="-sm" id="grossSalary" :maxlength="4"></input-item>
                      <input-append>万円</input-append>
                    </inputgroup-append>
                    <input-label>300万円の場合「300」※1万円単位、1万円未満の端数は切り捨てとなります。</input-label>
                  </input-group>
                </table-data>
              </table-row>
            </table-body> <!-- table-body -->
          </table-box>
        </table-group><!-- form -->

        <btngroup-double class="-margin-lg">
          <btn-main slot="main" class="-main" @click.prevent="submitEditWorkplace">
            確認画面へ
          </btn-main>
          <btn-back slot="back" class="-back -sm" routerName="mb023">
            戻る
          </btn-back>
        </btngroup-double>
      </section>
    </form>

  </div>
</template>

<script>
import { initCredentials } from '../utils/Auth'
import { editWorkplaceLambdaInvoke, getUserDataForMemberLambdaInvoke } from '../utils/LambdaHelper'
import { workForms, bizTypes, jobTypes, staffNums, inputItemsMb } from '../constants/constant'
import userData from '../components/mixin/UserData'
import HeadingOne from '../components/heading/HeadingOne'
import InputError from '../components/input/InputError'
import TooltipsItem from '../components/tooltip/TooltipsItem'
import BtnTooltip from '../components/button/BtnTooltip'
import BtnMain from '../components/button/BtnMain'
import BtnInput from '../components/button/BtnInput'
import BtnBack from '../components/button/BtnBack'
import BtngroupDouble from '../components/button/BtngroupDouble'
import HeadingTwo from '../components/heading/HeadingTwo'
import BtnRadio from '../components/button/BtnRadio'
import TableBox from '../components/tables/TableBox'
import TableBody from '../components/tables/TableBody'
import TableRow from '../components/tables/TableRow'
import TableHeader from '../components/tables/TableHeader'
import TableData from '../components/tables/TableData'
import BadgeIcon from '../components/badges/BadgeIcon'
import InputItem from '../components/input/InputItem'
import InputGroup from '../components/input/InputGroup'
import InputgroupAppend from '../components/input/InputgroupAppend'
import InputAppend from '../components/input/InputAppend'
import InputLabel from '../components/input/InputLabel'
import BtngroupRadio from '../components/button/BtngroupRadio'
import BtnSelect from '../components/button/BtnSelect'
import MainWizard from '../components/wizard/MainWizard'
import SubWizard from '../components/wizard/SubWizard'
import WizardGroup from '../components/wizard/WizardGroup'
import TableGroup from '../components/tables/TableGroup'
import InputPhone from '../components/input/InputPhone'
import ListGroup from '../components/list/ListGroup'
import ListItem from '../components/list/ListItem'
import ErrorsAlert from '../components/alert/ErrorsAlert'

export default {
  name: 'Mb-025',
  metaInfo: {
    title: 'お勤め先情報の変更｜お客さま情報の確認・変更',
    meta: [
      {name: 'description', content: 'au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。'},
      {name: 'keywords', content: 'au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,会員,会員サイト,お客さま情報'}
    ]
  },
  mixins: [userData],
  components: {
    ErrorsAlert,
    ListItem,
    ListGroup,
    InputPhone,
    TableGroup,
    WizardGroup,
    SubWizard,
    MainWizard,
    InputLabel,
    InputAppend,
    InputgroupAppend,
    InputGroup,
    InputItem,
    BadgeIcon,
    TableData,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
    BtnRadio,
    HeadingTwo,
    BtngroupDouble,
    TooltipsItem,
    BtnTooltip,
    InputError,
    HeadingOne,
    BtnMain,
    BtnBack,
    BtnInput,
    BtngroupRadio,
    BtnSelect
  },
  data () {
    return {
      breadcrumbItems: [{
        text: 'トップ',
        routerName: 'mb001'
      },
      {
        text: 'お客さま情報の確認・変更',
        routerName: 'mb023'
      },
      {
        text: 'お勤め先情報の変更',
        active: true
      }
      ],
      workForms: workForms,
      bizTypes: bizTypes,
      jobTypes: jobTypes,
      staffNums: staffNums,
      errors: {},
      isDisabled: false,
      input_gross_salary: '',
      userData: {
        work_form: '',
        biz_type: '',
        job_type: '',
        office_nm: '',
        office_phone_1: '',
        office_phone_2: '',
        office_phone_3: '',
        office_depart: '',
        nsh_ngt: '',
        staff_num: '',
        gross_salary: ''
      },
      nshNgtYyyy: '',
      nshNgtMm: '',
      inputItemsMb: inputItemsMb
    }
  },
  mounted () {
    try {
      getUserDataForMemberLambdaInvoke()
        .then(res => {
          for (let key in res) {
            if (this.userData.hasOwnProperty(key)) {
              this.userData[key] = res[key]
            }
          }
          this.setNshNgtYyyy()
          this.setNshNgtMm()
          if (this.userData.gross_salary) {
            this.input_gross_salary = this.userData.gross_salary / 10000
          }
        })
        .catch(error => {
          this.goToErrorPage(error)
        })
    } catch (e) {
      this.goToErrorPage(e)
    }
  },
  methods: {
    submitEditWorkplace: function (event) {
      try {
        if (this.isDisabled) {
          return
        }
        this.isDisabled = true
        this.errors = {}
        this.setNshNgt()
        this.setGrossSalary()
        initCredentials().then(res => {
          editWorkplaceLambdaInvoke(this.userData).then(res => {
            this.errors = res.errors || {}
            if (Object.keys(this.errors).length > 0) {
              this.scrollTop()
              this.isDisabled = false
            } else {
              this.$router.push({name: 'mb028'})
            }
          }).catch(error => {
            this.goToErrorPage(error)
          })
        }).catch(error => {
          this.goToErrorPage(error)
        })
      } catch (e) {
        this.goToErrorPage(e)
      }
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/pages/Mb025.vue